/**
 * Pestaña Seguidores del repositorio de Redes Sociales: curva de seguidores totales por red o
 * por perfil, nuevos / perdidos / neto por día, semana o mes, y tabla por perfil con inicio,
 * cierre y crecimiento del periodo. Día y semana salen de `maestro_seguidores_diario` (se
 * consulta solo el periodo); mes, de `maestro_seguidores_mensual` (ya cargado).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
    Bar, CartesianGrid, ComposedChart, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { ink, studio, FilterPill } from '../admin/studioUi';
import { downloadCsv, queryRepository } from './repoApi';
import NetworkDot from './NetworkDot';
import MobileCards from './MobileCards';
import InfoTip from './InfoTip';
import { rt, useFitColumns, HiddenColumnsNote } from './repoTable';
import { evaluateKpi, formatKpi } from './customKpis';
import {
    NETWORK_COLORS, bucketLabel, dayBefore, followerProfileSummary, followerSeries, formatCompact, formatNumber,
    formatPct, formatSigned, inPeriod, matchesSelection, monthEnd, monthsBetween, profileKey,
} from './socialRepoUtils';

const MAX_DAILY_MONTHS = 6;
const MAX_PROFILE_SERIES = 10;
const PROFILE_PALETTE = ['#5E35B1', '#1E88E5', '#D81B60', '#00897B', '#F4511E', '#6D4C41', '#3949AB', '#7CB342', '#8E24AA', '#FDD835'];
const GRAINS = [
    { id: 'day', label: 'Por día' },
    { id: 'week', label: 'Por semana' },
    { id: 'month', label: 'Por mes' },
];

const defaultGrain = (months) => (months <= 2 ? 'day' : months <= MAX_DAILY_MONTHS ? 'week' : 'month');

const SocialFollowers = ({ repo, kpis = [], onToggleExclude, followersMonthly, selection, period, dataVersion, narrow }) => {
    /* Aquí solo aplican los KPIs hechos con métricas de seguidores (Nuevos, Perdidos, Neto). */
    const followerKpis = useMemo(() => kpis.filter(kpi => kpi.groups.length === 1 && kpi.groups[0] === 'followers'), [kpis]);
    const months = monthsBetween(period.from, period.to).length;
    const dailyAllowed = months <= MAX_DAILY_MONTHS;
    const [grain, setGrain] = useState(() => defaultGrain(months));
    const [seriesBy, setSeriesBy] = useState('red');
    const [daily, setDaily] = useState({ loading: false, error: '', rows: [] });
    const [sort, setSort] = useState({ key: 'change', dir: 'desc' });

    useEffect(() => { setGrain(defaultGrain(months)); }, [months]);

    const effectiveGrain = dailyAllowed ? grain : 'month';
    const needsDaily = effectiveGrain !== 'month';
    const profileNames = useMemo(
        () => Array.from(new Set(selection.profiles.map(key => key.split('|').slice(1).join('|')))),
        [selection.profiles]
    );

    useEffect(() => {
        if (!needsDaily) return undefined;
        let alive = true;
        setDaily(prev => ({ ...prev, loading: true, error: '' }));
        const filters = [{ column: 'Fecha', operator: 'between', value: [dayBefore(`${period.from}-01`), monthEnd(period.to)] }];
        if (selection.networks.length) filters.push({ column: 'Red', operator: 'in', value: selection.networks });
        if (profileNames.length) filters.push({ column: 'Perfil', operator: 'in', value: profileNames });
        queryRepository(repo, [{
            id: 'daily',
            dataset: 'followersDaily',
            dimensions: ['Fecha', 'Red', 'Perfil', 'Fuente Total'],
            metrics: { 'Seguidores Totales': 'max', Nuevos: 'sum', Perdidos: 'sum', Neto: 'sum' },
            filters,
        }]).then((results) => {
            if (!alive) return;
            const result = results.daily;
            if (!result?.ok) throw new Error(result?.error || 'No se pudieron leer los seguidores diarios.');
            const rows = result.records.map(row => ({ ...row, Fecha: String(row.Fecha).slice(0, 10) }));
            setDaily({ loading: false, error: '', rows });
        }).catch((err) => {
            if (alive) setDaily({ loading: false, error: err.message, rows: [] });
        });
        return () => { alive = false; };
    }, [repo, needsDaily, period, selection.networks, profileNames, dataVersion]);

    /* Filas normalizadas: mismas columnas para el diario y el mensual (cierre al último día del mes). */
    const rows = useMemo(() => {
        if (needsDaily) return daily.rows.filter(row => matchesSelection(row, selection));
        const scoped = followersMonthly.filter(row => matchesSelection(row, selection));
        const before = scoped.filter(row => row.Mes < period.from);
        const lastBefore = new Map();
        before.forEach((row) => {
            const prev = lastBefore.get(profileKey(row));
            if (!prev || row.Mes > prev.Mes) lastBefore.set(profileKey(row), row);
        });
        const toDaily = (row, keepMovement) => ({
            ...row,
            Fecha: monthEnd(row.Mes),
            'Seguidores Totales': row['Seguidores al Cierre'],
            Nuevos: keepMovement ? row.Nuevos : 0,
            Perdidos: keepMovement ? row.Perdidos : 0,
            Neto: keepMovement ? row.Neto : 0,
        });
        return [
            ...Array.from(lastBefore.values()).map(row => toDaily(row, false)),
            ...inPeriod(scoped, period).map(row => toDaily(row, true)),
        ];
    }, [needsDaily, daily.rows, followersMonthly, selection, period]);

    const periodStart = `${period.from}-01`;
    const inside = useMemo(() => rows.filter(row => row.Fecha >= periodStart), [rows, periodStart]);
    const profilesSummary = useMemo(() => followerProfileSummary(rows, period), [rows, period]);

    /* Por perfil se grafican los de más seguidores; el resto queda en la tabla. */
    const topKeys = useMemo(() => new Set(
        [...profilesSummary]
            .sort((a, b) => (b.end?.value || 0) - (a.end?.value || 0))
            .slice(0, MAX_PROFILE_SERIES)
            .map(item => item.key)
    ), [profilesSummary]);

    const chart = useMemo(() => {
        const source = seriesBy === 'perfil' ? inside.filter(row => topKeys.has(profileKey(row))) : inside;
        const seriesOf = seriesBy === 'perfil' ? (row => `${row.Perfil} · ${row.Red}`) : (row => row.Red);
        const totalsSeries = followerSeries(source, { grain: effectiveGrain, seriesOf });
        const movementSeries = followerSeries(inside, { grain: effectiveGrain, seriesOf: row => row.Red });
        return {
            totals: totalsSeries.totals,
            series: totalsSeries.series,
            movement: movementSeries.movement.map(item => ({ ...item, PerdidosNeg: -item.Perdidos })),
        };
    }, [inside, topKeys, seriesBy, effectiveGrain]);

    const totals = useMemo(() => {
        const out = { start: 0, end: 0, Nuevos: 0, Perdidos: 0, Neto: 0, estimated: 0 };
        profilesSummary.forEach((item) => {
            if (item.start && item.end) {
                out.start += item.start.value;
                out.end += item.end.value;
            }
            out.Nuevos += item.Nuevos;
            out.Perdidos += item.Perdidos;
            out.Neto += item.Neto;
            out.estimated += item.estimated;
        });
        return { ...out, change: out.end - out.start, changePct: out.start ? (out.end - out.start) / out.start : null };
    }, [profilesSummary]);

    const sorted = useMemo(() => {
        const factor = sort.dir === 'asc' ? 1 : -1;
        const value = (item) => {
            switch (sort.key) {
                case 'Perfil': return item.Perfil;
                case 'Red': return item.Red;
                case 'start': return item.start?.value ?? -Infinity;
                case 'end': return item.end?.value ?? -Infinity;
                case 'changePct': return item.changePct ?? -Infinity;
                case 'kpi': return evaluateKpi(followerKpis.find(k => `kpi:${k.id}` === sort.kpiKey), item) ?? -Infinity;
                case 'change': return item.change ?? -Infinity;
                default: return item[sort.key] ?? -Infinity;
            }
        };
        return [...profilesSummary].sort((a, b) => {
            const va = value(a);
            const vb = value(b);
            return typeof va === 'string' ? va.localeCompare(vb, 'es') * factor : (va - vb) * factor;
        });
    }, [profilesSummary, sort, followerKpis]);

    const growthColor = v => (v > 0 ? '#137333' : v < 0 ? ink.danger : ink.text);
    const columns = [
        {
            key: 'Perfil', label: 'Perfil', min: 170,
            render: item => (
                <span style={styles.profileCell}>
                    <span style={styles.redCell}>
                        <NetworkDot red={item.Red} /><strong>{item.Perfil}</strong>
                        {item.estimated > 0 && <span style={styles.badge} title={`${item.estimated} días reconstruidos`}>estimado</span>}
                    </span>
                    <span style={styles.subText}>{item.Red}</span>
                </span>
            ),
        },
        {
            key: 'start', label: 'Al inicio', num: true, min: 78, priority: 4,
            info: { what: 'Seguidores totales al empezar el periodo.', how: 'Último total antes del primer día del periodo o, si no hay, el primero dentro del periodo. Pasa el cursor por la cifra para ver la fecha.' },
            title: item => item.start?.date || '', render: item => (item.start ? formatNumber(item.start.value) : '—'),
        },
        {
            key: 'end', label: 'Al cierre', num: true, min: 78, priority: 1,
            info: { what: 'Seguidores totales al terminar el periodo.', how: 'Último total disponible dentro del periodo.' },
            title: item => item.end?.date || '', render: item => (item.end ? formatNumber(item.end.value) : '—'),
        },
        {
            key: 'change', label: 'Crecimiento', num: true, info: 'crecimiento', min: 86, priority: 1,
            color: item => growthColor(item.change),
            render: item => <>{formatSigned(item.change)}<span style={styles.subNum}>{formatPct(item.changePct, 2)}</span></>,
        },
        { key: 'Nuevos', label: 'Nuevos', num: true, info: 'Nuevos', min: 68, priority: 3, render: item => formatNumber(item.Nuevos) },
        { key: 'Perdidos', label: 'Perdidos', num: true, info: 'Perdidos', min: 68, priority: 3, render: item => formatNumber(item.Perdidos) },
        { key: 'Neto', label: 'Neto', num: true, info: 'neto', min: 66, priority: 2, render: item => formatSigned(item.Neto) },
        ...followerKpis.map(kpi => ({
            key: `kpi:${kpi.id}`, label: kpi.name, num: true, kpi, info: 'kpiPropio', min: 80, priority: 5,
            color: () => ink.accentText, render: item => formatKpi(kpi, evaluateKpi(kpi, item)),
        })),
    ];
    const [tableRef, visibleColumns, hiddenColumns] = useFitColumns(columns, { reserve: onToggleExclude ? 64 : 0 });
    const toggleSort = (key) => {
        const next = key.startsWith('kpi:') ? { key: 'kpi', kpiKey: key } : { key };
        setSort(s => ((s.kpiKey || s.key) === key
            ? { ...s, dir: s.dir === 'asc' ? 'desc' : 'asc' }
            : { ...next, dir: key === 'Perfil' || key === 'Red' ? 'asc' : 'desc' }));
    };
    const sortedBy = (key) => (sort.key === 'kpi' ? sort.kpiKey === key : sort.key === key);

    const exportRows = () => downloadCsv(`redes-sociales-seguidores-${period.from}_${period.to}.csv`, [
        { label: 'Red', value: r => r.Red },
        { label: 'Perfil', value: r => r.Perfil },
        { label: 'Seguidores al inicio', value: r => r.start?.value ?? '' },
        { label: 'Fecha inicio', value: r => r.start?.date ?? '' },
        { label: 'Seguidores al cierre', value: r => r.end?.value ?? '' },
        { label: 'Fecha cierre', value: r => r.end?.date ?? '' },
        { label: 'Crecimiento', value: r => r.change ?? '' },
        { label: 'Crecimiento %', value: r => (r.changePct == null ? '' : r.changePct.toFixed(4)) },
        { label: 'Nuevos', value: r => r.Nuevos },
        { label: 'Perdidos', value: r => r.Perdidos },
        { label: 'Neto', value: r => r.Neto },
        { label: 'Días estimados', value: r => r.estimated },
        ...followerKpis.map(kpi => ({ label: kpi.name, value: r => evaluateKpi(kpi, r) ?? '' })),
    ], sorted);

    const colorOf = (name, index) => (seriesBy === 'red' ? (NETWORK_COLORS[name] || ink.accent) : PROFILE_PALETTE[index % PROFILE_PALETTE.length]);
    const loading = needsDaily && daily.loading;
    const tick = (bucket) => bucketLabel(bucket, effectiveGrain);

    return (
        <div style={styles.wrap}>
            <div style={styles.toolbar}>
                <FilterPill value={effectiveGrain} onChange={setGrain} style={{ minWidth: 150 }}>
                    {GRAINS.map(g => (
                        <option key={g.id} value={g.id} disabled={g.id !== 'month' && !dailyAllowed}>{g.label}</option>
                    ))}
                </FilterPill>
                <FilterPill value={seriesBy} onChange={setSeriesBy} style={{ minWidth: 170 }}>
                    <option value="red">Curva por red</option>
                    <option value="perfil">Curva por perfil</option>
                </FilterPill>
                {!dailyAllowed && <span style={studio.hint}>Con más de {MAX_DAILY_MONTHS} meses se agrupa por mes.</span>}
                {loading && <span style={studio.hint}>Consultando…</span>}
            </div>
            {daily.error && needsDaily && <div style={styles.error}>{daily.error}</div>}

            <div style={{ ...styles.kpiGrid, gridTemplateColumns: narrow ? 'repeat(2, minmax(0, 1fr))' : 'repeat(5, minmax(0, 1fr))' }}>
                <div style={styles.kpi} title={formatNumber(totals.end)}>
                    <span style={styles.kpiLabel}>Seguidores al cierre<InfoTip what="Suma de los seguidores totales de los perfiles filtrados al cierre del periodo." how="Último total diario (o cierre mensual en vista por mes) de cada perfil dentro del periodo." /></span>
                    <span style={styles.kpiValue}>{formatCompact(totals.end)}</span>
                    <span style={styles.kpiSub}>desde {formatCompact(totals.start)} al inicio</span>
                </div>
                <div style={styles.kpi}>
                    <span style={styles.kpiLabel}>Crecimiento<InfoTip term="crecimiento" /></span>
                    <span style={{ ...styles.kpiValue, color: totals.change >= 0 ? '#137333' : ink.danger }}>{formatSigned(totals.change)}</span>
                    <span style={styles.kpiSub}>{formatPct(totals.changePct, 2)} en el periodo</span>
                </div>
                <div style={styles.kpi}>
                    <span style={styles.kpiLabel}>Nuevos<InfoTip term="Nuevos" /></span>
                    <span style={styles.kpiValue}>{formatCompact(totals.Nuevos)}</span>
                    <span style={styles.kpiSub}>{formatNumber(totals.Nuevos)}</span>
                </div>
                <div style={styles.kpi}>
                    <span style={styles.kpiLabel}>Perdidos<InfoTip term="Perdidos" /></span>
                    <span style={styles.kpiValue}>{formatCompact(totals.Perdidos)}</span>
                    <span style={styles.kpiSub}>{formatNumber(totals.Perdidos)}</span>
                </div>
                <div style={styles.kpi}>
                    <span style={styles.kpiLabel}>Neto (movimiento)<InfoTip term="neto" /></span>
                    <span style={styles.kpiValue}>{formatSigned(totals.Neto)}</span>
                    <span style={styles.kpiSub}>nuevos − perdidos reportados</span>
                </div>
                {followerKpis.map(kpi => (
                    <div key={kpi.id} style={{ ...styles.kpi, borderLeftColor: '#F9AB00', backgroundColor: '#fffdf5' }} title={`Tu KPI: ${kpi.formula}`}>
                        <span style={studio.statLabel}>{kpi.name}</span>
                        <span style={styles.kpiValue}>{formatKpi(kpi, evaluateKpi(kpi, profilesSummary))}</span>
                        <span style={styles.kpiSub}>{kpi.formula}</span>
                    </div>
                ))}
            </div>

            <section style={studio.card}>
                <div style={styles.cardHead}>
                    <div>
                        <h3 style={styles.cardTitle}>Seguidores totales<InfoTip what="Evolución del total de seguidores." how="Para cada día, semana o mes se toma el último total conocido de cada perfil (si un perfil no reportó, se arrastra su valor anterior) y se suman por red o por perfil." /></h3>
                        <p style={studio.hint}>
                            {seriesBy === 'perfil' && profilesSummary.length > MAX_PROFILE_SERIES
                                ? `Los ${MAX_PROFILE_SERIES} perfiles con más seguidores · el resto está en la tabla`
                                : seriesBy === 'perfil' ? 'Por perfil' : 'Suma de los perfiles de cada red'}
                        </p>
                    </div>
                </div>
                <div style={{ width: '100%', height: narrow ? 240 : 300 }}>
                    <ResponsiveContainer>
                        <LineChart data={chart.totals} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid stroke={ink.line} vertical={false} />
                            <XAxis dataKey="bucket" tickFormatter={tick} tick={{ fontSize: 12, fill: ink.muted }} minTickGap={16} />
                            <YAxis tickFormatter={formatCompact} tick={{ fontSize: 12, fill: ink.muted }} width={56} domain={['auto', 'auto']} />
                            <Tooltip formatter={(value, name) => [formatNumber(value), name]} labelFormatter={tick} />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            {chart.series.map((name, index) => (
                                <Line key={name} type="monotone" dataKey={name} stroke={colorOf(name, index)} strokeWidth={2} dot={false} connectNulls />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </section>

            <section style={studio.card}>
                <div style={styles.cardHead}>
                    <div>
                        <h3 style={styles.cardTitle}>Nuevos y perdidos<InfoTip what="Altas y bajas de seguidores reportadas por las plataformas." how="Barras verdes = nuevos, rojas = perdidos (hacia abajo); la línea es el neto (nuevos − perdidos) de cada día, semana o mes." /></h3>
                        <p style={studio.hint}>Movimiento reportado por las plataformas · la línea es el neto</p>
                    </div>
                </div>
                <div style={{ width: '100%', height: narrow ? 220 : 260 }}>
                    <ResponsiveContainer>
                        <ComposedChart data={chart.movement} margin={{ top: 8, right: 12, left: 0, bottom: 0 }} stackOffset="sign">
                            <CartesianGrid stroke={ink.line} vertical={false} />
                            <XAxis dataKey="bucket" tickFormatter={tick} tick={{ fontSize: 12, fill: ink.muted }} minTickGap={16} />
                            <YAxis tickFormatter={formatCompact} tick={{ fontSize: 12, fill: ink.muted }} width={56} />
                            <Tooltip
                                formatter={(value, name) => [formatNumber(Math.abs(value)), name]}
                                labelFormatter={tick}
                            />
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            <ReferenceLine y={0} stroke={ink.lineStrong} />
                            <Bar dataKey="Nuevos" stackId="mov" fill="#34A853" maxBarSize={28} />
                            <Bar dataKey="PerdidosNeg" name="Perdidos" stackId="mov" fill="#EA4335" maxBarSize={28} />
                            <Line type="monotone" dataKey="Neto" stroke={ink.text} strokeWidth={2} dot={false} />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </section>

            <section style={studio.card}>
                <div style={styles.cardHead}>
                    <div>
                        <h3 style={styles.cardTitle}>Perfiles</h3>
                        <p style={studio.hint}>
                            {sorted.length} perfiles
                            {totals.estimated > 0 && <> · «estimado» = días reconstruidos<InfoTip term="estimado" /></>}
                        </p>
                    </div>
                    <button type="button" onClick={exportRows} style={studio.ghostBtn} disabled={!sorted.length}>Exportar CSV</button>
                </div>
                {narrow ? (
                    <MobileCards
                        items={sorted}
                        keyOf={item => item.key}
                        titleOf={item => <><NetworkDot red={item.Red} />{item.Perfil}{item.estimated > 0 && <span style={styles.badge}>estimado</span>}</>}
                        subtitleOf={item => `${item.Red} · ${item.start ? formatNumber(item.start.value) : '—'} → ${item.end ? formatNumber(item.end.value) : '—'}`}
                        metricsOf={item => [
                            { label: 'Crecimiento', value: formatSigned(item.change), color: item.change > 0 ? '#137333' : item.change < 0 ? ink.danger : undefined },
                            { label: '%', value: formatPct(item.changePct, 2) },
                            { label: 'Neto', value: formatSigned(item.Neto) },
                            { label: 'Nuevos', value: formatNumber(item.Nuevos) },
                            { label: 'Perdidos', value: formatNumber(item.Perdidos) },
                            ...followerKpis.map(kpi => ({ label: kpi.name, value: formatKpi(kpi, evaluateKpi(kpi, item)), color: ink.accentText })),
                        ]}
                        actionOf={onToggleExclude ? (item => <button type="button" style={styles.omitBtn} onClick={() => onToggleExclude([item.key])}>Omitir</button>) : undefined}
                        empty="Sin datos para estos filtros."
                    />
                ) : (
                <div ref={tableRef}>
                    <table style={rt.table}>
                        <thead>
                            <tr>
                                {visibleColumns.map(col => (
                                    <th key={col.key} onClick={() => toggleSort(col.key)} style={{ ...rt.th, cursor: 'pointer', textAlign: col.num ? 'right' : 'left', ...(col.kpi ? { color: ink.accentText } : {}) }}>
                                        {col.label}{sortedBy(col.key) ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                                        {typeof col.info === 'string' && <InfoTip term={col.info} how={col.kpi ? `Fórmula: ${col.kpi.formula}.` : undefined} />}
                                        {col.info && typeof col.info === 'object' && <InfoTip what={col.info.what} how={col.info.how} />}
                                    </th>
                                ))}
                                {onToggleExclude && <th style={{ ...rt.th, width: 60 }} />}
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map(item => (
                                <tr key={item.key}>
                                    {visibleColumns.map(col => (
                                        <td
                                            key={col.key}
                                            title={col.title ? col.title(item) : undefined}
                                            style={{ ...(col.num ? rt.num : rt.td), ...(col.color ? { color: col.color(item) } : {}) }}
                                        >
                                            {col.render(item)}
                                        </td>
                                    ))}
                                    {onToggleExclude && (
                                        <td style={{ ...rt.td, textAlign: 'right' }}>
                                            <button type="button" style={styles.omitBtn} title="Quitar este perfil de todos los cálculos" onClick={() => onToggleExclude([item.key])}>Omitir</button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                            {!sorted.length && !loading && (
                                <tr><td colSpan={visibleColumns.length + 1} style={{ ...rt.td, textAlign: 'center', color: ink.faint }}>Sin datos para estos filtros.</td></tr>
                            )}
                        </tbody>
                    </table>
                    <HiddenColumnsNote hidden={hiddenColumns} />
                </div>
                )}
            </section>
        </div>
    );
};

const styles = {
    wrap: { display: 'flex', flexDirection: 'column', gap: 18 },
    toolbar: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' },
    error: { padding: '10px 12px', borderRadius: 10, backgroundColor: ink.dangerSoft, color: ink.danger, fontSize: '0.86rem' },
    kpiGrid: { display: 'grid', gap: 10 },
    kpi: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: '10px 14px',
        backgroundColor: ink.surface,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderLeftWidth: 3,
        borderLeftColor: ink.accent,
        borderRadius: 12,
        minWidth: 0,
    },
    kpiValue: { fontSize: '1.3rem', fontWeight: 600, color: ink.text, letterSpacing: '-0.03em', lineHeight: 1.15 },
    profileCell: { display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 },
    subText: { fontSize: '0.7rem', color: ink.faint, marginLeft: 14 },
    subNum: { display: 'block', fontSize: '0.7rem', opacity: 0.8 },
    kpiLabel: { ...studio.statLabel, display: 'inline-flex', alignItems: 'center' },
    omitBtn: {
        padding: '3px 9px',
        borderRadius: 999,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.lineStrong,
        backgroundColor: ink.surface,
        color: ink.muted,
        fontSize: '0.72rem',
        fontFamily: 'inherit',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    kpiSub: { fontSize: '0.76rem', color: ink.faint },
    cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 14 },
    cardTitle: { margin: 0, fontSize: '1.05rem', fontWeight: 600, color: ink.text },
    numTd: { ...studio.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
    redCell: { display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' },
    badge: {
        marginLeft: 8,
        padding: '1px 7px',
        borderRadius: 999,
        backgroundColor: '#fef7e0',
        color: '#8a6100',
        fontSize: '0.68rem',
        fontWeight: 700,
        verticalAlign: 'middle',
    },
};

export default SocialFollowers;
